import { TextInput, Textarea, Stack, Switch, Select } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect } from "react";
import { CptModifier } from "../../../services/admin/cptmodifiers.service";
import { useBillingScenariosAdmin } from "../../../hooks/admin/useBillingScenarios";
import {
  required,
  compose,
  maxLength,
  scrollToFirstError,
} from "../../../utils/formValidation";

export interface CptModifierFormValues {
  code: string;
  description: string;
  billingScenarioId: string;
  isActive: boolean;
}

interface CptModifierFormProps {
  initialValues?: CptModifier;
  onSubmit: (values: CptModifierFormValues) => void;
}

export default function CptModifierForm({
  initialValues,
  onSubmit,
}: CptModifierFormProps) {
  const { data: billingScenarios = [] } = useBillingScenariosAdmin();

  const form = useForm<CptModifierFormValues>({
    initialValues: {
      code: initialValues?.code || "",
      description: initialValues?.description || "",
      billingScenarioId: initialValues?.billingScenarioId || "",
      isActive: initialValues?.isActive ?? true,
    },
    validate: {
      code: compose(
        required("Modifier code is required"),
        maxLength(10, "Modifier code must be 10 characters or less")
      ),
      description: required("Description is required"),
    },
  });

  // Reset form when initialValues change (switching between add/edit)
  useEffect(() => {
    form.setValues({
      code: initialValues?.code || "",
      description: initialValues?.description || "",
      billingScenarioId: initialValues?.billingScenarioId || "",
      isActive: initialValues?.isActive ?? true,
    });
    form.clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const billingScenarioOptions = billingScenarios
    .filter((s: any) => s.isActive !== false)
    .map((s: any) => ({ value: s.id, label: s.name }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label));

  return (
    <form
      id="cpt-modifier-form"
      noValidate
      onSubmit={form.onSubmit(onSubmit, () => scrollToFirstError(form))}
    >
      <Stack gap="md">
        <TextInput
          label="Modifier Code"
          placeholder="e.g., 26, TC"
          withAsterisk
          {...form.getInputProps("code")}
        />

        <Textarea
          label="Description"
          placeholder="Enter modifier description"
          minRows={3}
          withAsterisk
          {...form.getInputProps("description")}
        />

        <Select
          label="Billing Scenario"
          placeholder="Select billing scenario (optional)"
          description="Associate this modifier with a specific billing scenario"
          data={billingScenarioOptions}
          searchable
          clearable
          {...form.getInputProps("billingScenarioId")}
        />

        <Switch
          label="Active"
          description="Inactive modifiers will not be available for billing"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}
